import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const labelClass = "block text-[10px] font-bold uppercase tracking-widest text-on-surface-variant mb-0.5";

const Register = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    name: '', phone: '', email: '', password: '', confirmPassword: '',
    role: 'customer', businessName: '', address: '', city: '', state: '', pincode: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [toast, setToast] = useState(null);
  const toastKey = React.useRef(0);

  const showToast = (msg, type = 'error') => {
    toastKey.current += 1;
    setToast({ msg, type, key: toastKey.current });
  };

  const inputClass = (field) =>
  `w-full px-3 py-1.5 bg-surface-container-low border rounded-lg focus:ring-1 focus:bg-white transition-all outline-none placeholder:text-outline-variant text-xs ${
    errors[field]
      ? 'border-red-400 focus:ring-red-300'
      : 'border-outline-variant/30 focus:ring-primary'
  }`;

  const handleChange = (e) => {
    let value = e.target.value;
    if (e.target.name === 'phone') {
      value = value.replace(/^\+91/, '').replace(/\D/g, '').slice(0, 10);
    }
    setFormData({ ...formData, [e.target.name]: value });
    setErrors({ ...errors, [e.target.name]: '' });
  };

  const handleSubmit = async () => {
  // Frontend validation
  const newErrors = {};

  // Name
  if (!formData.name.trim() || formData.name.trim().length < 2)
    newErrors.name = 'Enter a valid full name (min 2 chars)';
  else if (!/^[a-zA-Z\s'-]+$/.test(formData.name.trim()))
    newErrors.name = 'Name can only contain letters, spaces, hyphens';

  // Phone
  if (!formData.phone)
    newErrors.phone = 'Mobile number is required';
  else if (!/^[6-9]\d{9}$/.test(formData.phone))
    newErrors.phone = 'Enter a valid 10-digit Indian mobile number';

  // Email
  if (!formData.email)
    newErrors.email = 'Email address is required';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(formData.email))
    newErrors.email = 'Enter a valid email address';

  // Password
  if (!formData.password)
    newErrors.password = 'Password is required';
  else if (formData.password.length < 6)
    newErrors.password = 'Password must be at least 6 characters';
  else if (!/[A-Za-z]/.test(formData.password) || !/[0-9]/.test(formData.password))
    newErrors.password = 'Password must contain letters and numbers';

  // Confirm Password
  if (!formData.confirmPassword)
    newErrors.confirmPassword = 'Please confirm your password';
  else if (formData.password !== formData.confirmPassword)
    newErrors.confirmPassword = 'Passwords do not match';

  // Business fields
  if (showBusinessFields) {
    if (!formData.businessName.trim())
      newErrors.businessName = 'Business name is required';
    else if (formData.businessName.trim().length < 3)
      newErrors.businessName = 'Business name too short (min 3 chars)';

    if (!formData.address.trim())
      newErrors.address = 'Address is required';
    else if (formData.address.trim().length < 10)
      newErrors.address = 'Enter a complete address (min 10 chars)';

    if (!formData.city.trim())
      newErrors.city = 'City is required';
    else if (!/^[a-zA-Z\s]+$/.test(formData.city.trim()))
      newErrors.city = 'Enter a valid city name';

    if (!formData.state.trim())
      newErrors.state = 'State is required';
    else if (!/^[a-zA-Z\s]+$/.test(formData.state.trim()))
      newErrors.state = 'Enter a valid state name';

    if (!formData.pincode)
      newErrors.pincode = 'Pincode is required';
    else if (!/^\d{6}$/.test(formData.pincode))
      newErrors.pincode = 'Enter a valid 6-digit pincode';
  }
  if (Object.keys(newErrors).length > 0) {
    setErrors(newErrors);
    // Show toast with first error
    const firstErrorMsg = Object.values(newErrors)[0];
    showToast(firstErrorMsg, 'error');
    setLoading(false);
    return;
  }

  setLoading(true);
  try {
    const response = await api.post('/api/auth/register', formData);
    login(response.data);
    showToast('Account created! Welcome aboard.', 'success');
    setTimeout(() => {
      switch (response.data.role) {
        case 'farmer':  navigate('/farmer'); break;
        case 'butcher': navigate('/butcher'); break;
        default:        navigate('/');
      }
    }, 1500);
  } catch (err) {
    const msg = err.response?.data?.message || 'Registration failed. Please try again.';
    if (msg.includes('Name'))                                      { setErrors({ name: msg }); showToast(msg, 'error'); }
    else if (msg.includes('phone') || msg.includes('Phone'))       { setErrors({ phone: msg }); showToast(msg, 'error'); }
    else if (msg.includes('email') || msg.includes('Email'))       { setErrors({ email: msg }); showToast(msg, 'error'); }
    else if (msg.includes('Password') || msg.includes('password')) { setErrors({ password: msg }); showToast(msg, 'error'); }
    else if (msg.includes('Business'))                             { setErrors({ businessName: msg }); showToast(msg, 'error'); }
    else if (msg.includes('Address'))                              { setErrors({ address: msg }); showToast(msg, 'error'); }
    else if (msg.includes('City'))                                 { setErrors({ city: msg }); showToast(msg, 'error'); }
    else if (msg.includes('State'))                                { setErrors({ state: msg }); showToast(msg, 'error'); }
    else if (msg.includes('Pincode'))                              { setErrors({ pincode: msg }); showToast(msg, 'error'); }
    else                                                           { setErrors({ general: msg }); showToast(msg, 'error'); }
  } finally {
    setLoading(false);
  }
};

  const showBusinessFields = formData.role === 'farmer' || formData.role === 'butcher';

  const roles = [
    { value: 'customer', icon: 'shopping_cart', label: 'Buy Animals' },
    { value: 'farmer', icon: 'storefront', label: 'Sell Animals' },
    { value: 'butcher', icon: 'store', label: 'Butcher / Shop' },
  ];

  return (
    <div className="fixed inset-0 font-body">
      {toast && (
       <div key={toast.key} className={`fixed top-5 left-1/2 -translate-x-1/2 z-50 shadow-lg rounded-xl overflow-hidden text-xs font-semibold min-w-[220px] ${
          toast.type === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-500 text-white'
        }`}>
          <div className="flex items-center gap-2 px-4 py-2.5">
            <span className="material-symbols-outlined" style={{fontSize:'15px'}}>
              {toast.type === 'success' ? 'check_circle' : 'error'}
            </span>
            <span className="flex-1">{toast.msg}</span>
            <button onClick={() => setToast(null)} className="opacity-70 hover:opacity-100">
              <span className="material-symbols-outlined" style={{fontSize:'13px'}}>close</span>
            </button>
          </div>
        </div>
      )}

      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img
          className="w-full h-full object-cover brightness-75"
          src="https://lh3.googleusercontent.com/aida-public/AB6AXuDfsl01tTn-hVDG7p-WvDCVX3XdN_BGl1NvnPvkgV5vwh0JWPoCa7GXgE6dDRnDTrNey4uw-t6iHSW5ufw7qjCw5_IMW_E6PQGHubjCpmuGL9m4uC87jkpdIE00XlDaJpJKYraCCrQJ0_LaqbKVg1qck_8PrllLS3TV3L_Aw3QkftgJIHIbK7JMD4oqLHC2JyisWL22tulMprWo3pEZ0kQBgKYMZWbk8RlMlXXjKJ7zBoEb7Iks8DnFMFjzWeYHrONEZ7a_JXv8NsCj"
          alt="" aria-hidden="true"
        />
        <div className="absolute inset-0 backdrop-blur-md bg-black/30"></div>
      </div>

      {/* Backdrop click to close */}
      <div
        className="relative z-10 h-full flex items-center justify-center px-4"
        onClick={() => navigate('/')}
      >
        {/* Modal */}
        <div
          className="w-full max-w-4xl bg-white rounded-2xl overflow-hidden flex shadow-2xl"
          style={{ maxHeight: '96vh' }}
          onClick={(e) => e.stopPropagation()}
        >

          {/* Left — Image */}
          <div className="hidden md:flex relative md:w-[52%] flex-shrink-0">
            <img
              alt="Champion Goat"
              className="absolute inset-0 w-full h-full object-cover object-center"
              src="https://lh3.googleusercontent.com/aida-public/AB6AXuBNZjE4zecpdmjUeAVC4rPYP7OQqi16zylXNEwpeRDGgV9CE3azczQymWXOe_4TXDs5QXqwHDYUHuq5_1M6RmGRmeXnxcoo7HxsNAxmHwRJ_26uxoRJNEo-MmVMRtaSDkBV76VXzlnMfhziQQbaMH5pAlOoGpHi66hxpY_Ehrfv6YN8uSolFNNU0goPgZB8Tq3tTLaIy1o7lxJbbHU2sG0tPz4HlEX5tLtDDKneefPgBr3QZ6ssnOIk50w7LbMNj_UX-ZVHKyUJjyGd"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/20 to-transparent"></div>
            <div className="relative mt-auto p-6 text-white">
              <img src="/assets/elite-goats.png" alt="Elite Goats"
                className="h-8 w-auto object-contain mb-4 brightness-0 invert opacity-90" />
              <h2 className="font-headline text-lg font-extrabold tracking-tight leading-tight mb-1.5">
                Join the Elite Network of Breeders.
              </h2>
              <p className="text-white/75 text-xs leading-relaxed">
                Connecting premium livestock owners across India with the modern marketplace.
              </p>
              <div className="mt-5 flex items-center gap-3">
                <div className="flex -space-x-2">
                  {['bg-zinc-200','bg-zinc-300','bg-zinc-400'].map((c,i) => (
                    <div key={i} className={`w-6 h-6 rounded-full border-2 border-white ${c}`}></div>
                  ))}
                </div>
                <span className="text-xs font-medium">5k+ Breeders Joined</span>
              </div>
            </div>
          </div>

          {/* Right — Form */}
          <div className="flex flex-col p-5 overflow-y-auto w-full" style={{ maxHeight: '96vh' }}>

            {/* Close */}
            <div className="flex justify-end mb-1">
              <button onClick={() => navigate('/')}
                className="text-outline hover:text-on-surface p-1 rounded-full hover:bg-surface-container-low transition-colors">
                <span className="material-symbols-outlined" style={{fontSize:'18px'}}>close</span>
              </button>
            </div>

            {/* Heading */}
            <div className="mb-3">
              <h1 className="font-headline text-base font-bold text-on-surface tracking-tight">
                Create your account
              </h1>
              <p className="text-secondary text-[11px]">Start trading the finest livestock in India today.</p>
            </div>

            <div className="flex flex-col gap-2.5">

              {errors.general && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-1.5 rounded-lg text-[11px]">
                  {errors.general}
                </div>
              )}

              {/* Role */}
              <div>
                <span className={labelClass}>I want to</span>
                <div className="grid grid-cols-3 gap-1.5">
                  {roles.map((r) => (
                    <button key={r.value} type="button"
                      onClick={() => setFormData({...formData, role: r.value})}
                      className={`py-1.5 px-1 rounded-lg border-2 flex flex-col items-center gap-0.5 transition-all ${
                        formData.role === r.value
                          ? 'border-primary bg-primary/5'
                          : 'border-transparent bg-surface-container hover:border-outline-variant'
                      }`}
                    >
                      <span className={`material-symbols-outlined ${formData.role === r.value ? 'text-primary' : 'text-outline'}`}
                        style={{fontSize:'16px'}}>
                        {r.icon}
                      </span>
                      <span className="text-[10px] font-semibold leading-tight text-center">{r.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Name + Phone row */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={labelClass}>Full Name</label>
                  <input type="text" name="name" value={formData.name}
                    onChange={handleChange} className={inputClass('name')}
                    placeholder="Arjun Singh" autoComplete="name" required />
                    {errors.name && <p className="mt-0.5 text-[10px] text-red-500 font-semibold flex items-center gap-0.5"><span className="material-symbols-outlined" style={{fontSize:'11px'}}>error</span>{errors.name}</p>}
                </div>
                <div>
                  <label className={labelClass}>Mobile</label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-2.5 flex items-center text-[10px] font-bold text-outline pointer-events-none">+91</span>
                    <input type="tel" name="phone" value={formData.phone}
                      onChange={handleChange}
                      className={`${inputClass('phone')} pl-8`}
                      placeholder="98765 43210" maxLength={10} pattern="[0-9]{10}"
                      autoComplete="tel" required />
                  </div>
                  {errors.phone && <p className="mt-0.5 text-[10px] text-red-500 font-semibold flex items-center gap-0.5">
                    <span className="material-symbols-outlined" style={{fontSize:'11px'}}>error</span>{errors.phone}
                  </p>}
                </div>
              </div>

              {/* Email */}
              <div>
                <label className={labelClass}>Email Address</label>
                <input type="email" name="email" value={formData.email}
                  onChange={handleChange} className={inputClass('email')} 
                  placeholder="arjun@example.com" autoComplete="email" required />
                {errors.email && <p className="mt-0.5 text-[10px] text-red-500 font-semibold flex items-center gap-0.5"><span className="material-symbols-outlined" style={{fontSize:'11px'}}>error</span>{errors.email}</p>}
              </div>

              {/* Business fields */}
              {showBusinessFields && (
                <div className="flex flex-col gap-2 pt-1.5 border-t border-outline-variant/20">
                  <span className={labelClass}>
                    {formData.role === 'farmer' ? 'Farm Details' : 'Shop Details'}
                  </span>
                  <input type="text" name="businessName" value={formData.businessName}
                    onChange={handleChange} className={inputClass('businessName')}
                    placeholder={formData.role === 'farmer' ? 'Farm name' : 'Shop name'} required />
                    {errors.businessName && <p className="mt-0.5 text-[10px] text-red-500 font-semibold flex items-center gap-0.5"><span className="material-symbols-outlined" style={{fontSize:'11px'}}>error</span>{errors.businessName}</p>}
                  <input type="text" name="address" value={formData.address}
                    onChange={handleChange} className={inputClass('address')}
                    placeholder="Complete address" required />
                  <div className="grid grid-cols-3 gap-1.5">
                    {['city','state','pincode'].map((f) => (
                      <div key={f}> 
                        <input type="text" name={f} value={formData[f]}
                          onChange={handleChange} className={inputClass(f)}
                          placeholder={f.charAt(0).toUpperCase()+f.slice(1)} required />
                            {errors[f] && <p className="mt-0.5 text-[10px] text-red-500 font-semibold flex items-center gap-0.5"><span className="material-symbols-outlined" style={{fontSize:'11px'}}>error</span>{errors[f]}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Password row */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={labelClass}>Password</label>
                  <div className="relative">
                    <input type={showPassword ? 'text' : 'password'} name="password"
                      value={formData.password} onChange={handleChange}
                      className={`${inputClass('password')} pr-8`}
                      placeholder="Min. 6 chars" autoComplete="new-password" required />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-2 flex items-center text-outline hover:text-on-surface">
                      <span className="material-symbols-outlined" style={{fontSize:'16px'}}>
                        {showPassword ? 'visibility_off' : 'visibility'}
                      </span>
                    </button>
                  </div>
                  {errors.password && <p className="mt-0.5 text-[10px] text-red-500 font-semibold flex items-center gap-0.5"><span className="material-symbols-outlined" style={{fontSize:'11px'}}>error</span>{errors.password}</p>}
                </div>
                <div>
                    <label className={labelClass}>Confirm Password</label>
                    <div className="relative">
                      <input type={showConfirm ? 'text' : 'password'} name="confirmPassword"
                        value={formData.confirmPassword} onChange={handleChange}
                        className={`${inputClass('confirmPassword')} pr-8`}
                        placeholder="Re-enter" autoComplete="new-password" required />
                      <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                        className="absolute inset-y-0 right-2 flex items-center text-outline hover:text-on-surface">
                        <span className="material-symbols-outlined" style={{fontSize:'16px'}}>
                          {showConfirm ? 'visibility_off' : 'visibility'}
                        </span>
                      </button>
                    </div>
                    {errors.confirmPassword && <p className="mt-0.5 text-[10px] text-red-500 font-semibold flex items-center gap-0.5"><span className="material-symbols-outlined" style={{fontSize:'11px'}}>error</span>{errors.confirmPassword}</p>}
                  </div>
              </div>

              {/* Terms */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox"
                  className="w-3 h-3 text-primary border-outline-variant rounded focus:ring-primary"
                  required />
                <span className="text-[11px] text-secondary">
                  I agree to the{' '}
                  <Link to="/terms" className="text-primary font-bold hover:underline"
                    onClick={e => e.stopPropagation()}>Terms</Link>
                  {' '}&{' '}
                  <Link to="/privacy" className="text-primary font-bold hover:underline"
                    onClick={e => e.stopPropagation()}>Privacy Policy</Link>
                </span>
              </label>

              {/* Submit */}
              <button type="button" onClick={handleSubmit} disabled={loading}
                className="w-full py-2 rounded-full bg-gradient-to-r from-primary to-primary-container text-white font-headline font-bold text-xs shadow-md hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                {loading ? 'Creating Account...' : 'Register Now'}
              </button>

              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-outline-variant/30"></div>
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-white px-3 text-[10px] text-outline font-bold uppercase tracking-widest">
                    Or register with
                  </span>
                </div>
              </div>

              {/* Social */}
              <div className="grid grid-cols-2 gap-2">
                <button type="button"
                  className="flex items-center justify-center gap-2 py-1.5 rounded-full border border-outline-variant/30 hover:bg-surface-container transition-colors text-xs font-semibold">
                  🌐 Google
                </button>
                <button type="button"
                  className="flex items-center justify-center gap-2 py-1.5 rounded-full border border-outline-variant/30 hover:bg-surface-container transition-colors text-xs font-semibold text-emerald-600">
                  💬 WhatsApp
                </button>
              </div>

              {/* Login link */}
              <p className="text-center text-[11px] text-secondary">
                Already have an account?{' '}
                <Link to="/login" className="text-primary font-bold hover:underline">Sign In</Link>
              </p>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;